import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackPhoneClick, trackWhatsAppClick } from "@/components/ConversionTracking";

interface ProminentCTAProps {
  title: string;
  description?: string;
  primaryCTA?: {
    text: string;
    href: string;
    external?: boolean;
  };
  secondaryCTA?: {
    text: string;
    href: string;
    external?: boolean;
  };
  showContactButtons?: boolean;
  variant?: "default" | "gradient" | "minimal";
  className?: string;
}

export const ProminentCTA = ({
  title,
  description,
  primaryCTA = { text: "Request a Quote", href: "/contact" },
  secondaryCTA,
  showContactButtons = false,
  variant = "default",
  className,
}: ProminentCTAProps) => {
  const renderButton = (cta: { text: string; href: string; external?: boolean }, isPrimary: boolean) => {
    const buttonContent = (
      <Button
        size="lg"
        variant={isPrimary ? "default" : "outline"}
        className={cn(
          "w-full sm:w-auto group min-h-[44px] touch-manipulation",
          isPrimary && "bg-accent hover:bg-accent/90 text-accent-foreground shadow-gold"
        )}
      >
        {cta.text}
        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Button>
    );

    if (cta.external) {
      return (
        <a href={cta.href} target="_blank" rel="noopener noreferrer">
          {buttonContent}
        </a>
      );
    }

    return <Link to={cta.href}>{buttonContent}</Link>;
  };

  return (
    <section
      className={cn(
        "py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-12",
        variant === "gradient" && "bg-gradient-to-br from-accent/5 via-background to-accent/10",
        variant === "default" && "bg-muted/30",
        variant === "minimal" && "bg-transparent",
        className
      )}
    >
      <div className="container mx-auto max-w-4xl text-center">
        <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-primary">
          {title}
        </h2>
        {description && (
          <p className="font-inter text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6">
          {renderButton(primaryCTA, true)}
          {secondaryCTA && renderButton(secondaryCTA, false)}
        </div>

        {showContactButtons && (
          <div className="mt-8 pt-8 border-t border-border/20">
            <p className="font-inter text-sm text-muted-foreground mb-4">
              Or reach out directly:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" size="sm" asChild>
                <a href="tel:+34688495309" onClick={trackPhoneClick}>
                  <Phone className="h-4 w-4 mr-2" />
                  Call Expert
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a
                  href="https://wa.me/34688495309?text=Hello%20Aureum%20VII%20Trading,%20I%27m%20interested%20in%20your%20smart%20toilet%20solutions."
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackWhatsAppClick}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="mailto:info@aureum7trading.com?subject=Product%20Inquiry">
                  <Mail className="h-4 w-4 mr-2" />
                  Email Us
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
